/**
 * Scooth Pet — Announcement bar.
 *
 * Two custom elements:
 *   <scooth-announcement-bar>   layout mode + auto-rotation + a11y controls
 *   <scooth-shipping-progress>  free-delivery progress, synced to the cart
 *
 * Deliberately built on plain HTMLElement rather than the theme's Component base
 * class. This file is loaded on every page in the header, so keeping it free of
 * framework coupling means a Horizon upgrade cannot break the announcement bar,
 * and the module has no import cost on the critical path.
 */

/**
 * Resolve a usable width for an element.
 *
 * Inside the theme editor an iframe can report a width of 0 for an element that
 * is in fact laid out, which makes matchMedia and naive clientWidth reads
 * unreliable. Walking up to the first ancestor with a non-zero width sidesteps
 * that, falling back to the viewport as a last resort.
 *
 * @param {HTMLElement} element
 * @returns {number}
 */
function reliableWidth(element) {
  /** @type {HTMLElement | null} */
  let node = element;

  while (node) {
    const width = node.clientWidth || node.getBoundingClientRect().width;
    if (width > 0) return width;
    node = node.parentElement;
  }

  return window.innerWidth || 0;
}

class ScoothAnnouncementBar extends HTMLElement {
  /** @type {HTMLElement[]} */
  #items = [];
  #index = 0;
  /** @type {number | null} */
  #timer = null;
  #suspended = false;
  #stopped = false;
  /** @type {ResizeObserver | null} */
  #observer = null;
  /** @type {HTMLButtonElement | null} */
  #pauseButton = null;

  connectedCallback() {
    this.#items = /** @type {HTMLElement[]} */ (Array.from(this.querySelectorAll('.scooth-abar__item')));
    if (this.#items.length === 0) return;

    const current = this.#items.findIndex((item) => item.hasAttribute('data-current'));
    this.#index = current > -1 ? current : 0;

    this.#pauseButton = this.querySelector('[data-scooth-abar-pause]');
    this.#pauseButton?.addEventListener('click', this.#toggleStopped);

    this.addEventListener('mouseenter', this.#suspend);
    this.addEventListener('mouseleave', this.#resume);
    this.addEventListener('focusin', this.#suspend);
    this.addEventListener('focusout', this.#resume);
    document.addEventListener('visibilitychange', this.#handleVisibilityChange);

    if (this.dataset.display === 'adaptive') {
      this.#observer = new ResizeObserver(this.#measure);
      this.#observer.observe(this);
      this.#measure();
    } else {
      this.#setMode(this.dataset.display === 'rotate' ? 'rotate' : 'inline');
    }
  }

  disconnectedCallback() {
    this.#stop();
    this.#observer?.disconnect();
    this.#pauseButton?.removeEventListener('click', this.#toggleStopped);
    this.removeEventListener('mouseenter', this.#suspend);
    this.removeEventListener('mouseleave', this.#resume);
    this.removeEventListener('focusin', this.#suspend);
    this.removeEventListener('focusout', this.#resume);
    document.removeEventListener('visibilitychange', this.#handleVisibilityChange);
  }

  /** Rotation interval in milliseconds. */
  get #interval() {
    const seconds = Number.parseFloat(this.getAttribute('autoplay') ?? '5');
    return Number.isFinite(seconds) && seconds > 0 ? seconds * 1000 : 5000;
  }

  #measure = () => {
    const minimum = Number.parseInt(this.dataset.inlineMinWidth ?? '750', 10);
    this.#setMode(reliableWidth(this) >= minimum ? 'inline' : 'rotate');
  };

  /** @param {'inline' | 'rotate'} mode */
  #setMode(mode) {
    if (this.dataset.mode === mode) return;
    this.dataset.mode = mode;

    if (mode === 'rotate') {
      this.#render();
      this.#start();
      return;
    }

    // Inline mode shows every item, so a "current" item is meaningless.
    this.#stop();
    for (const item of this.#items) item.removeAttribute('data-current');
  }

  #render() {
    const total = this.#items.length;
    if (total === 0) return;

    const index = ((this.#index % total) + total) % total;
    this.#items.forEach((item, position) => item.toggleAttribute('data-current', position === index));
  }

  #start = () => {
    this.#stop();

    if (this.#stopped || this.#suspended) return;
    if (this.dataset.mode !== 'rotate') return;
    if (this.#items.length < 2) return;

    this.#timer = window.setInterval(() => {
      if (document.hidden || this.matches(':hover')) return;
      this.#index += 1;
      this.#render();
    }, this.#interval);
  };

  #stop() {
    if (this.#timer === null) return;
    window.clearInterval(this.#timer);
    this.#timer = null;
  }

  #suspend = () => {
    this.#suspended = true;
    this.#stop();
  };

  #resume = () => {
    this.#suspended = false;
    this.#start();
  };

  #handleVisibilityChange = () => {
    if (document.hidden) this.#stop();
    else this.#start();
  };

  #toggleStopped = () => {
    this.#stopped = !this.#stopped;
    this.#pauseButton?.setAttribute('aria-pressed', String(this.#stopped));

    if (this.#pauseButton) {
      this.#pauseButton.textContent = this.#stopped ? 'Resume announcements' : 'Pause announcements';
    }

    if (this.#stopped) this.#stop();
    else this.#start();
  };
}

class ScoothShippingProgress extends HTMLElement {
  /** @type {HTMLElement | null} */
  #text = null;
  /** @type {HTMLElement | null} */
  #meter = null;
  /** @type {string[]} */
  #eventNames = [];
  /** @type {number | null} */
  #debounce = null;

  connectedCallback() {
    this.#text = this.querySelector('[data-progress-text]');
    this.#meter = this.querySelector('[data-progress-meter]');

    void this.#bind();
  }

  disconnectedCallback() {
    for (const name of this.#eventNames) {
      document.removeEventListener(name, this.#handleCartUpdate);
    }
    this.#eventNames = [];

    if (this.#debounce !== null) window.clearTimeout(this.#debounce);
  }

  /**
   * Subscribe to cart updates.
   *
   * Horizon dispatches the Shopify standard cart event, whose name is imported
   * rather than hard-coded so it survives a rename upstream. The import is
   * dynamic and guarded: if the module map ever drops `@shopify/events`, the
   * element degrades to the legacy event name instead of throwing and taking
   * the whole bar down with it.
   */
  async #bind() {
    /** @type {Set<string>} */
    const names = new Set(['cart:update']);

    try {
      const { StandardEvents } = await import('@shopify/events');
      if (StandardEvents?.cartLinesUpdate) names.add(StandardEvents.cartLinesUpdate);
    } catch {
      // Fallback name already queued.
    }

    for (const name of names) {
      document.addEventListener(name, this.#handleCartUpdate);
      this.#eventNames.push(name);
    }
  }

  #handleCartUpdate = () => {
    // Add-to-cart flows can emit several events in one interaction; coalesce them.
    if (this.#debounce !== null) window.clearTimeout(this.#debounce);
    this.#debounce = window.setTimeout(() => void this.#refresh(), 60);
  };

  async #refresh() {
    const root = window.Shopify?.routes?.root ?? '/';

    try {
      const response = await fetch(`${root}cart.js`, { headers: { Accept: 'application/json' } });
      if (!response.ok) return;

      const cart = await response.json();
      this.#update(Number(cart?.total_price ?? 0), Number(cart?.item_count ?? 0));
    } catch {
      // Leave the server-rendered state in place.
    }
  }

  /**
   * @param {number} totalPrice - Cart total in the shop's minor currency unit.
   * @param {number} itemCount
   */
  #update(totalPrice, itemCount) {
    const threshold = Number.parseInt(this.dataset.threshold ?? '0', 10);
    if (!Number.isFinite(threshold) || threshold <= 0) return;

    const remaining = Math.max(threshold - totalPrice, 0);

    let state = 'empty';
    if (itemCount > 0 && remaining > 0) state = 'progress';
    else if (itemCount > 0) state = 'unlocked';

    this.dataset.state = state;

    if (this.#text) {
      const template =
        state === 'progress'
          ? this.dataset.textProgress ?? ''
          : state === 'unlocked'
            ? this.dataset.textUnlocked ?? ''
            : this.dataset.textEmpty ?? '';

      this.#text.textContent = template.replace('[amount]', this.#money(remaining));
    }

    const percentage = Math.min(100, Math.round((totalPrice / threshold) * 100));
    this.#meter?.style.setProperty('--progress', `${percentage}%`);
    this.#meter?.setAttribute('aria-valuenow', String(percentage));
  }

  /**
   * @param {number} cents
   * @returns {string}
   */
  #money(cents) {
    const currency = this.dataset.currency || 'GBP';
    const locale = (this.dataset.locale || 'en-GB').replace('_', '-');
    const amount = cents / 100;

    try {
      return new Intl.NumberFormat(locale, {
        style: 'currency',
        currency,
        // Whole amounts read cleaner as £12 than £12.00 in a one-line bar.
        minimumFractionDigits: cents % 100 === 0 ? 0 : 2,
        maximumFractionDigits: 2,
      }).format(amount);
    } catch {
      return amount.toFixed(2);
    }
  }
}

if (!customElements.get('scooth-announcement-bar')) {
  customElements.define('scooth-announcement-bar', ScoothAnnouncementBar);
}

if (!customElements.get('scooth-shipping-progress')) {
  customElements.define('scooth-shipping-progress', ScoothShippingProgress);
}
